/**
 * Unified Action Center hook - single RPC for assignments + other actions.
 * Replaces useOrgAssignments + useMyAssignments + useTasks for Action Center.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../shared/services/supabase';
import type { OrgAssignment, Assignment, OtherAction } from '../types';

export interface PaymentOverrideRequest {
  id: string;
  campaign_id: string;
  campaign_link_id: string;
  campaign_title: string | null;
  creator_username: string | null;
  suggested_amount: number;
  requested_amount: number;
  currency: string;
  reason: string | null;
  created_at: string;
}

interface ActionsResponse {
  assignments: OrgAssignment[] | Assignment[];
  other_actions: OtherAction[];
  payment_override_requests?: PaymentOverrideRequest[];
  action_count: number;
}

function assignmentNeedsCreatorAction(assignment: Assignment): boolean {
  // request_testimonial: creator waits; brand submits via invite link
  const isCreatorActionStep = (stepType: string, status: string) =>
    (stepType === 'confirm_brief' && status === 'active') ||
    (stepType === 'content_review' && (status === 'active' || status === 'changes_requested')) ||
    (stepType === 'submit_deliverables' && status === 'active');

  if (Array.isArray(assignment.steps_summary)) {
    for (const step of assignment.steps_summary) {
      if (isCreatorActionStep(step.step_type, step.status)) return true;
    }
  }

  const current = assignment.current_step;
  if (current) {
    return isCreatorActionStep(current.step_type, current.status);
  }

  return false;
}

function otherActionFromRow(row: Record<string, unknown>): OtherAction {
  const type = (row.type as string) ?? 'campaign_complete';
  return {
    id: String(row.id ?? ''),
    type: type as OtherAction['type'],
    token: (row.token as string | null) ?? null,
    email: (row.email as string | null) ?? null,
    created_at: String(row.created_at ?? ''),
    expires_at: (row.expires_at as string | null) ?? null,
    campaign_id: (row.campaign_id as string | null) ?? null,
    campaign_title: (row.campaign_title as string | null) ?? null,
    creator_profile_id: (row.creator_profile_id as string | null) ?? null,
    creator: (row.creator as { id: string; username: string | null; email: string | null } | null) ?? null,
  } as OtherAction;
}

interface UseActionsOptions {
  organizationId: string | null | undefined;
  profileId: string | null | undefined;
  role: 'org' | 'creator';
  countOnly?: boolean;
  /** When true (org only): returns slim summaries for list; use portal_get_assignment_details for full details on open */
  summaryOnly?: boolean;
}

export function useActions({
  organizationId,
  profileId,
  role,
  countOnly = false,
  summaryOnly = false,
}: UseActionsOptions) {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['actions', organizationId ?? 'all', profileId, role, countOnly, summaryOnly],
    queryFn: async (): Promise<ActionsResponse> => {
      if (!profileId) {
        return { assignments: [], other_actions: [], payment_override_requests: [], action_count: 0 };
      }
      if (role === 'org' && organizationId === undefined && !countOnly) {
        return { assignments: [], other_actions: [], payment_override_requests: [], action_count: 0 };
      }

      const { data: result, error: rpcError } = await supabase.rpc('portal_get_actions', {
        p_organization_id: role === 'org' ? organizationId ?? null : null,
        p_profile_id: profileId,
        p_role: role,
        p_count_only: countOnly,
        p_summary_only: summaryOnly,
      });

      if (rpcError) {
        throw new Error(rpcError.message);
      }

      const res = result as ActionsResponse | null;
      if (!res) {
        return { assignments: [], other_actions: [], action_count: 0 };
      }

      const assignments = (res.assignments ?? []) as OrgAssignment[] | Assignment[];

      const other_actions: OtherAction[] = Array.isArray(res.other_actions)
        ? (res.other_actions as unknown as Record<string, unknown>[]).map((r) => otherActionFromRow(r))
        : [];

      const derivedCreatorCount =
        role === 'creator' && !countOnly && Array.isArray(assignments)
          ? (assignments as Assignment[]).filter(assignmentNeedsCreatorAction).length
          : null;

      return {
        assignments,
        other_actions,
        action_count: derivedCreatorCount ?? (res.action_count ?? 0),
      };
    },
    enabled:
      !!profileId &&
      (role !== 'org' || organizationId !== undefined || countOnly) &&
      (role === 'creator' || organizationId !== undefined || countOnly),
  });

  const queryClient = useQueryClient();
  const queryKey = ['actions', organizationId ?? 'all', profileId, role, countOnly, summaryOnly] as const;

  const declineTask = async (taskId: string) => {
    const { error } = await supabase
      .from('invites')
      .update({ declined_at: new Date().toISOString() })
      .eq('id', taskId);

    if (error) throw new Error(error.message);
    await queryClient.invalidateQueries({ queryKey });
  };

  return {
    assignments: data?.assignments ?? [],
    otherActions: data?.other_actions ?? [],
    paymentOverrideRequests: data?.payment_override_requests ?? [],
    actionCount: data?.action_count ?? 0,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    refetch,
    declineTask,
  };
}
